import { Component, DestroyRef, Input, OnChanges, OnInit, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { BookingUpgradeStateMap, CartridgeBookingService } from '@markant-platform/cartridge/feature-booking';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { ActivatedRoute } from '@angular/router';
import { UsageRightService } from '@markant-platform/usage-right/data-access';
import {
  BookingDataWithUpgradeInfo,
  Cartridge,
  CartridgeStatus,
  CartridgeVariant,
} from '@markant-platform/cartridge/model';
import { BookingStateMap } from '@markant-platform/shared/cartridge/model';
import { ManagementData } from './model/management.model';
import { UsageRight, UsageRightState } from '@markant-platform/usage-right/model';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { StaticRole } from '@markant-platform/shared/user/model';
import { DateFormatOptions } from '@jsverse/transloco-locale';
import { BookingState } from '@markant-platform/shared/backoffice/model';
import {
  getDisableSuggestUpgrade,
  getSuggestUpgradeTooltipTranslationKey,
  hasAvailableUpgradeCandidates,
  isBookingActive,
  isBulkPrice,
} from '@markant-platform/shared/util-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { OrganizationDetailData, QualificationLevel } from 'data-model';

@Component({
  selector: 'markant-platform-management-view',
  templateUrl: './management-view.component.html',
  styleUrls: ['./management-view.component.scss'],
})
export class ManagementViewComponent implements OnInit, OnChanges {
  //if used as a demo component this value will be used instead of fetched data
  @Input() demoContent?: ManagementData;

  managementData$: Observable<ManagementData> = of();
  managementDataUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  organizationData!: OrganizationDetailData;
  bookingData = signal<BookingDataWithUpgradeInfo | undefined>(undefined);
  usageRight?: UsageRight;
  bookingId!: string;
  isPlatformAdmin$!: Observable<boolean>;
  isServiceAdmin$!: Observable<boolean>;
  isOrgAdmin$!: Observable<boolean>;
  isLoading = false;
  isUsageRightEditModalOpen = false;
  isBulkPrice = false;
  isOpenVariantUpgradeWizard = false;
  isUpgradable = false;
  isOrgQualificationLevel3 = false;
  dateFormatOptions: DateFormatOptions = {
    dateStyle: 'long',
    day: undefined,
    year: undefined,
    month: undefined,
  };
  canBlockService = false;
  serviceBlocked = false;
  isConfirmServiceBlockDialogOpen = false;
  isSuggestUpgradeDialogOpen = false;
  allowSuggestUpgrade = false;
  disableSuggestUpgrade = true;
  suggestUpgradeTooltip = this.translocoService.translate('cartridge.management.suggestUpgrade.tooltip.available');

  readonly bookingStateMap = BookingStateMap;
  readonly bookingUpgradeStateMap = BookingUpgradeStateMap;

  protected readonly UsageRightState = UsageRightState;

  constructor(
    private readonly cartridgesFacade: CartridgesFacade,
    private readonly cartridgeBookingService: CartridgeBookingService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly usageRightService: UsageRightService,
    private readonly organizationService: OrganizationService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly destroyRef: DestroyRef,
    readonly translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    if (this.demoContent) {
      this.managementData$ = of(this.demoContent);
      this.isOrgQualificationLevel3 = true;
      this.bookingData.set({ serviceUpgradeRequestData: undefined } as BookingDataWithUpgradeInfo);
      return;
    }

    this.bookingId = this.activatedRoute.snapshot?.params['bookingId'];
    this.managementData$ = this.getManagementData(this.bookingId);
    this.isPlatformAdmin$ = this.authenticatedUserService
      .getUserRoles()
      .pipe(map(roles => roles.some(({ name }) => name === StaticRole.PLATFORM_ADMIN)));
    this.isServiceAdmin$ = this.authenticatedUserService
      .getUserRoles()
      .pipe(map(roles => roles.some(({ name }) => name === StaticRole.LEISTUNG_ADMIN)));
    this.isOrgAdmin$ = this.authenticatedUserService
      .getUserRoles()
      .pipe(map(roles => roles.some(({ name }) => name === StaticRole.ORGANIZATION_ADMIN)));
  }

  ngOnChanges(): void {
    if (this.demoContent) {
      this.managementData$ = of(this.demoContent);
      return;
    }
    this.managementData$ = this.getManagementData(this.bookingId);
  }

  onCloseUsageRightEditDialog(isSuccessCreated: boolean): void {
    this.isUsageRightEditModalOpen = false;
    this.managementDataUpdated.next(isSuccessCreated);
  }

  onBookingUpdated(): void {
    this.managementDataUpdated.next(true);
  }

  async blockConfirmation(): Promise<void> {
    try {
      await firstValueFrom(this.cartridgeBookingService.blockUnblockService(this.bookingId, !this.serviceBlocked));

      this.managementDataUpdated.next(true);
    } finally {
      this.isConfirmServiceBlockDialogOpen = false;
    }
  }

  confirmServiceBlockDialogToggle(open: boolean): void {
    this.isConfirmServiceBlockDialogOpen = open;
  }

  openVariantUpgradeWizard(open: boolean): void {
    this.isOpenVariantUpgradeWizard = open;
  }

  private getManagementData(bookingId: string): Observable<ManagementData> {
    return this.cartridgesFacade.loaded$.pipe(
      tap(() => (this.isLoading = true)),
      filter(loaded => loaded),
      take(1),
      switchMap(() =>
        combineLatest([
          this.cmsLanguageService.activeCMSLanguage$.pipe(distinctUntilChanged()),
          this.managementDataUpdated.asObservable(),
        ])
      ),
      switchMap(() => this.getManagementDataByBookingId(bookingId)),
      catchError(() => {
        this.isLoading = false;
        return of();
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private getManagementDataByBookingId(bookingId: string): Observable<ManagementData> {
    return this.cartridgeBookingService.getBookingById(bookingId).pipe(
      switchMap(bookingData => {
        return combineLatest([
          this.organizationService.getOrganizationDetailsById(bookingData.organizationId),
          this.cartridgesFacade.cartridgeById$(bookingData.cartridgeId),
          this.getUsageRightData(bookingData.organizationId, bookingData.id, bookingData.bookingState),
        ]).pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
          map(([organization, cartridge, usageRight]) => {
            const cartridgeVariant = cartridge?.variants.find(
              ({ variantId }) => variantId === bookingData.cartridgeVariantId
            ) as CartridgeVariant;

            this.isLoading = false;
            this.isBulkPrice = isBulkPrice(bookingData.price);
            this.isOrgQualificationLevel3 = organization.qualificationLevel === QualificationLevel.LEVEL_3;
            this.usageRight = usageRight;
            this.canBlockService = cartridge?.status === 'ACTIVE' && !!bookingData.upgradableCartridgeVariantIds.length;
            this.serviceBlocked = !!bookingData.blocked;
            this.organizationData = organization;
            this.bookingData.set(bookingData);

            if (cartridge) {
              this.isUpgradable = this.getIsUpgradable(cartridge);
              this.allowSuggestUpgrade = this.getAllowSuggestUpgrade(cartridge);
              this.disableSuggestUpgrade = getDisableSuggestUpgrade(this.bookingData() as BookingDataWithUpgradeInfo);
              this.suggestUpgradeTooltip = this.translocoService.translate(
                getSuggestUpgradeTooltipTranslationKey(this.bookingData() as BookingDataWithUpgradeInfo)
              );
            }

            return this.mapManagementData(bookingData, cartridgeVariant, cartridge as Cartridge, usageRight);
          })
        );
      })
    );
  }

  private getUsageRightData(
    organizationId: string,
    bookingId: string,
    state: BookingState
  ): Observable<UsageRight | undefined> {
    return combineLatest([this.isPlatformAdmin$, this.isOrgAdmin$]).pipe(
      switchMap(([isPlatformAdmin, isOrgAdmin]) => {
        if (!['PENDING', 'ACCEPTED', 'DECLINED'].includes(state)) {
          if (isPlatformAdmin) {
            return this.usageRightService.getUsageRightForOrganizationByBookingId(organizationId, bookingId);
          } else if (isOrgAdmin) {
            return this.usageRightService.getUsageRightForCurrentOrgByBookingId(bookingId);
          }
        }
        return of(undefined);
      })
    );
  }

  private mapManagementData(
    bookingData: BookingDataWithUpgradeInfo,
    cartridgeVariant: CartridgeVariant,
    cartridge: Cartridge,
    usageRight: UsageRight | undefined
  ): ManagementData {
    const {
      id,
      blocked,
      cartridgeVariantName,
      cancellationDate,
      bookingState,
      cartridgeId,
      cartridgeVariantId,
      priceRequestId,
      price,
      setUpCost,
      bookingDate,
      workflowFinishedAt,
      bookingStartedAt,
      responsibleUserId,
      responsibleUserFirstname,
      responsibleUserLastname,
      organizationId,
      serviceUpgradeRequestData,
      contractStartDate,
      serviceUpgradeState,
    } = bookingData;

    const { variantStatus, calculationParameters } = cartridgeVariant;
    const { variants, cartridgeServicePackages } = cartridge;
    const usageRightState = usageRight?.state;
    return {
      id,
      cartridgeVariantName,
      cancellationDate,
      bookingState,
      cartridgeId,
      cartridgeVariantId,
      priceRequestId,
      price,
      setUpCost,
      calculationParameters,
      cartridgeServicePackages,
      organizationId,
      serviceUpgradeRequestData,
      serviceManagerId: responsibleUserId,
      serviceManager: `${responsibleUserFirstname} ${responsibleUserLastname}`,
      bookingDate: this.convertToDate(bookingDate),
      workflowFinishedAt: this.convertToDate(workflowFinishedAt),
      bookingStartedAt: this.convertToDate(bookingStartedAt),
      usageRightActivationState: this.usageRightService.getActivationState(bookingData.bookingState),
      singleVariant: this.isSingleVariant(variantStatus, variants),
      ...(contractStartDate && {
        contractStartDate: this.convertToDate(contractStartDate),
      }),
      serviceUpgradeState,
      blocked: !!blocked,
      usageRightState,
    };
  }

  private convertToDate(date?: string): Date | undefined {
    return date ? new Date(date) : undefined;
  }

  private isSingleVariant(variantStatus: CartridgeStatus, variants: CartridgeVariant[]): boolean {
    const allowUiBookingLength = variants.filter(v => v.allowUiBooking).length;

    return (
      (variantStatus === CartridgeStatus.INACTIVE && allowUiBookingLength === 0) ||
      (variantStatus === CartridgeStatus.ACTIVE && allowUiBookingLength < 2)
    );
  }

  private getIsUpgradable(service: Cartridge): boolean {
    if (!this.organizationData.ezrCustomer || !this.isServiceActive(service)) return false;

    const { bookingState, serviceUpgradeRequestData, upgradableCartridgeVariantIds } =
      this.bookingData() as BookingDataWithUpgradeInfo;

    if (!isBookingActive(bookingState) || serviceUpgradeRequestData) return false;

    return hasAvailableUpgradeCandidates(upgradableCartridgeVariantIds);
  }

  private getAllowSuggestUpgrade(service: Cartridge): boolean {
    if (!this.isServiceActive(service)) {
      return false;
    }

    const { bookingState, upgradableCartridgeVariantIds } = this.bookingData() as BookingDataWithUpgradeInfo;

    return isBookingActive(bookingState) && hasAvailableUpgradeCandidates(upgradableCartridgeVariantIds);
  }

  private isServiceActive(service: Cartridge): boolean {
    return service?.status === CartridgeStatus.ACTIVE;
  }

  toggleSuggestUpgradeDialogConfirmation(): void {
    this.isSuggestUpgradeDialogOpen = !this.isSuggestUpgradeDialogOpen;
  }

  async confirmSuggestUpgrade(): Promise<void> {
    try {
      await firstValueFrom(this.cartridgeBookingService.suggestUpgradeService(this.bookingId));

      this.managementDataUpdated.next(true);
    } finally {
      this.toggleSuggestUpgradeDialogConfirmation();
    }
  }
}
